import { authHeaders, clearWebSession, getWebSession, type WebProfile } from "./webSession";
import type {
  Bundle,
  BundleLine,
  CategoryRef,
  Story,
  CartLine,
  DeliveryMethod,
  Locale,
  LoyaltyState,
  Order,
  OrderStatus,
  PaymentMethod,
  PaymentState,
  PersonalPromo,
  Post,
  PostSummary,
  PostType,
  Product,
  ProductCategory,
  PromoPreview,
  Review,
} from "@/types";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") || "http://localhost:3000";

/** Uploaded photos are served by the API ("/uploads/…"); anything else is used as-is. */
export function mediaUrl(src: string | null | undefined, width?: 200 | 400 | 800): string | undefined {
  if (!src) return undefined;
  if (!src.startsWith("/uploads/")) return src;
  // Lighter copies for cards and thumbnails (generated once on the server).
  return `${API_BASE_URL}${src}${width ? `?w=${width}` : ""}`;
}

// ---- Content language. Set by I18nProvider; Russian is the base language. ----
let currentLocale: Locale = "ru";
export function setApiLocale(locale: Locale): void {
  currentLocale = locale;
}

type Translatable = { translations?: unknown };
function pickLang<T extends object>(item: Translatable): Partial<T> {
  if (currentLocale === "ru" || !item.translations || typeof item.translations !== "object") return {};
  const tr = (item.translations as Record<string, unknown>)[currentLocale];
  if (!tr || typeof tr !== "object") return {};
  // Only non-empty strings override the Russian text.
  return Object.fromEntries(
    Object.entries(tr).filter(([, v]) => (typeof v === "string" ? v.trim() !== "" : v && typeof v === "object" && Object.keys(v).length > 0)),
  ) as Partial<T>;
}

export function localizeProduct(p: Product): Product {
  const tr = pickLang<{ name: string; shortDescription: string; description: string; attributes: Record<string, string> }>(p);
  return { ...p, ...tr, attributes: tr.attributes ?? p.attributes, categoryRef: p.categoryRef ? localizeCategory(p.categoryRef) : p.categoryRef };
}

/** Category name in the customer's language (falls back to Russian). */
export function localizeCategory<C extends CategoryRef>(c: C): C {
  const name = currentLocale === "ru" ? undefined : (c.translations as Record<string, { name?: string }> | null | undefined)?.[currentLocale]?.name?.trim();
  return name ? { ...c, name } : c;
}

export async function fetchCategories(): Promise<CategoryRef[]> {
  return (await http<CategoryRef[]>("/api/categories")).map(localizeCategory);
}

function localizePost<T extends PostSummary>(p: T & Translatable): T {
  return { ...p, ...pickLang<{ title: string; excerpt: string; body: string }>(p) };
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        // Only declare a JSON body when there IS one — Fastify rejects an
        // empty body sent as application/json (400).
        ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...authHeaders(),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError("Network error", 0, "NETWORK");
  }

  if (response.status === 401 && getWebSession()) clearWebSession();
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let code: string | undefined;
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await response.json()) as { error?: { message?: string; code?: string; details?: Record<string, unknown> } | string; message?: string; code?: string };
      // Our API: { error: { message, code } }; Fastify default: { error: "Bad Request", message, code }
      const e = typeof body.error === "object" ? body.error : undefined;
      message = e?.message ?? body.message ?? message;
      code = e?.code ?? body.code;
      details = e?.details;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, response.status, code, details);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

// ---- Catalog ----

export async function fetchProducts(filters?: { category?: string; search?: string }): Promise<Product[]> {
  const params = new URLSearchParams();
  if (filters?.category) params.set("category", filters.category);
  if (filters?.search) params.set("search", filters.search);
  const qs = params.toString();
  return (await http<Product[]>(`/api/products${qs ? `?${qs}` : ""}`)).map(localizeProduct);
}

export async function fetchProduct(slug: string): Promise<Product | undefined> {
  try {
    return localizeProduct(await http<Product>(`/api/products/${encodeURIComponent(slug)}`));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return undefined;
    throw err;
  }
}

export async function fetchBestsellers(): Promise<Product[]> {
  return (await http<Product[]>("/api/products/bestsellers")).map(localizeProduct);
}

// ---- Orders ----

interface BackendOrder {
  id: string;
  number: string;
  createdAt: string;
  status: Order["status"];
  paymentStatus: Order["paymentStatus"];
  items: Order["items"];
  deliveryMethod: DeliveryMethod;
  deliveryFeeUzs: number;
  paymentMethod: PaymentMethod;
  subtotalUzs: number;
  discountUzs: number;
  totalUzs: number;
  fullName: string;
  phone: string;
  address: string | null;
  comment: string | null;
  cancellableUntil: string;
  paymentDeadline: string;
  cancelReason: string | null;
  arrivingAt?: string | null;
  promoRedemption?: { promoCode: { code: string } } | null;
  deliveryDate?: string | null;
  deliverySlot?: string | null;
  giftCardUzs?: number;
  isGift?: boolean;
  giftCode?: string | null;
  giftLink?: string | null;
  giftClaimedAt?: string | null;
  giftRecipientName?: string | null;
  feedback?: { rating: number } | null;
  statusEvents?: Array<{ status: OrderStatus; createdAt: string }>;
}

function mapOrder(raw: BackendOrder): Order {
  return {
    id: raw.id,
    number: raw.number,
    createdAt: raw.createdAt,
    status: raw.status,
    paymentStatus: raw.paymentStatus,
    items: raw.items,
    deliveryMethod: raw.deliveryMethod,
    deliveryFeeUzs: raw.deliveryFeeUzs,
    paymentMethod: raw.paymentMethod,
    subtotalUzs: raw.subtotalUzs,
    discountUzs: raw.discountUzs ?? 0,
    promoCode: raw.promoRedemption?.promoCode.code,
    totalUzs: raw.totalUzs,
    address:
      raw.deliveryMethod !== "pickup"
        ? { fullName: raw.fullName, phone: raw.phone, address: raw.address ?? "", comment: raw.comment ?? undefined }
        : undefined,
    cancellableUntil: raw.cancellableUntil,
    paymentDeadline: raw.paymentDeadline,
    cancelReason: raw.cancelReason,
    arrivingAt: raw.arrivingAt ?? null,
    deliveryDate: raw.deliveryDate ?? null,
    deliverySlot: raw.deliverySlot ?? null,
    giftCardUzs: raw.giftCardUzs ?? 0,
    isGift: !!raw.isGift,
    giftCode: raw.giftCode ?? null,
    giftLink: raw.giftLink ?? null,
    giftClaimedAt: raw.giftClaimedAt ?? null,
    giftRecipientName: raw.giftRecipientName ?? null,
    feedbackRating: raw.feedback?.rating ?? null,
    statusHistory: raw.statusEvents ?? [],
  };
}

export async function fetchOrders(): Promise<Order[]> {
  return (await http<BackendOrder[]>("/api/orders")).map(mapOrder);
}

export async function fetchOrder(id: string): Promise<Order | undefined> {
  try {
    return mapOrder(await http<BackendOrder>(`/api/orders/${encodeURIComponent(id)}`));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return undefined;
    throw err;
  }
}

export async function fetchPaymentState(orderId: string): Promise<PaymentState> {
  return http<PaymentState>(`/api/orders/${encodeURIComponent(orderId)}/payment`);
}

export interface PlaceOrderInput {
  lines: CartLine[];
  deliveryMethod: DeliveryMethod;
  paymentMethod: PaymentMethod;
  contact: { fullName: string; phone: string; address?: string; comment?: string };
  promoCode?: string;
  acceptedTerms: boolean;
  marketingConsent?: boolean;
  bundles?: BundleLine[];
  deliveryDate?: string;
  deliverySlot?: string;
  giftCardCode?: string;
  gift?: { message?: string; fromName?: string };
}

export interface PlaceOrderResult {
  order: Order;
  checkoutUrl?: string;
  giftLink?: string;
}

export async function placeOrder(input: PlaceOrderInput): Promise<PlaceOrderResult> {
  const result = await http<{ order: BackendOrder; checkoutUrl?: string; giftLink?: string }>("/api/orders", { method: "POST", body: JSON.stringify(input) });
  return { order: mapOrder(result.order), checkoutUrl: result.checkoutUrl, giftLink: result.giftLink };
}

export async function cancelOrder(orderId: string): Promise<{ ok: boolean }> {
  await http(`/api/orders/${encodeURIComponent(orderId)}/cancel`, { method: "POST" });
  return { ok: true };
}

// ---- Loyalty & promo ----

export async function fetchLoyalty(): Promise<LoyaltyState> {
  return http<LoyaltyState>("/api/loyalty");
}

export async function fetchMyPromos(): Promise<PersonalPromo[]> {
  return http<PersonalPromo[]>("/api/promo/mine");
}

export async function validatePromo(code: string, lines: CartLine[], deliveryMethod: DeliveryMethod = "pickup", bundles: BundleLine[] = []): Promise<PromoPreview> {
  return http<PromoPreview>("/api/promo/validate", { method: "POST", body: JSON.stringify({ code, lines, bundles, deliveryMethod }) });
}

// ---- Content ----

export async function fetchPosts(params?: { type?: Exclude<PostType, "page">; home?: boolean; limit?: number }): Promise<PostSummary[]> {
  const q = new URLSearchParams();
  if (params?.type) q.set("type", params.type);
  if (params?.home) q.set("home", "1");
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return (await http<Array<PostSummary & Translatable>>(`/api/posts${qs ? `?${qs}` : ""}`)).map((p) => localizePost(p));
}

export async function fetchPost(slug: string): Promise<{ post: Post; products: Product[] } | undefined> {
  try {
    const result = await http<{ post: Post & Translatable; products: Product[] }>(`/api/posts/${encodeURIComponent(slug)}`);
    return { post: localizePost(result.post), products: result.products.map(localizeProduct) };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return undefined;
    throw err;
  }
}

// ---- Reviews ----

export async function fetchReviews(productSlug: string, limit = 3, offset = 0): Promise<{ items: Review[]; total: number }> {
  return http(`/api/products/${encodeURIComponent(productSlug)}/reviews?limit=${limit}&offset=${offset}`);
}

/** Latest approved reviews across the shop (home block and "all reviews" page). */
export async function fetchLatestReviews(limit = 3, offset = 0): Promise<{ items: Review[]; total: number }> {
  const res = await http<{ items: Array<Review & { product?: { name: string; slug: string; translations?: unknown } }>; total: number }>(
    `/api/reviews?limit=${limit}&offset=${offset}`,
  );
  return {
    total: res.total,
    items: res.items.map((r) => ({
      ...r,
      product: r.product ? { slug: r.product.slug, name: pickLang<{ name: string }>(r.product).name ?? r.product.name } : undefined,
    })),
  };
}

export async function fetchMyReviews(): Promise<Array<{ productId: string; status: "pending" | "approved" | "rejected"; rating: number }>> {
  return http("/api/reviews/mine");
}

export async function submitReview(input: { productId: string; rating: number; text: string; photos?: string[] }): Promise<void> {
  await http("/api/reviews", { method: "POST", body: JSON.stringify(input) });
}

// ---- Requests ----

export interface B2BLeadInput {
  companyName: string;
  contactPerson: string;
  phone: string;
  interest: string;
  volume: string;
  comment?: string;
}

export async function submitB2BLead(input: B2BLeadInput): Promise<{ ok: boolean }> {
  const result = await http<{ ok: boolean; leadId: string }>("/api/b2b-leads", { method: "POST", body: JSON.stringify(input) });
  return { ok: result.ok };
}

export type InquiryPurpose = "home" | "office" | "cafe" | "restock";

export interface ProductInquiryInput {
  productId: string;
  fullName: string;
  phone: string;
  purpose: InquiryPurpose;
  comment?: string;
}

export async function submitProductInquiry(input: ProductInquiryInput): Promise<{ ok: boolean }> {
  const result = await http<{ ok: boolean; inquiryId: string }>("/api/product-inquiries", { method: "POST", body: JSON.stringify(input) });
  return { ok: result.ok };
}

// ---- Optional features, marketing consent, referrals, subscriptions ----

export interface SeasonTheme {
  preset: string;
  announcement: Record<Locale, string>;
  eyebrow: Record<Locale, string>;
  title1: Record<Locale, string>;
  title2: Record<Locale, string>;
  text: Record<Locale, string>;
  heroBg: string;
  heroArtBg: string;
  /** Own banner photo ("/uploads/…") or null for the brand pack. */
  heroImage: string | null;
}

export interface Exhibition {
  enabled: boolean;
  eyebrow?: Record<Locale, string>;
  title: Record<Locale, string>;
  text: Record<Locale, string>;
  source: string;
}

/** Desktop website photo slots and social links (Settings → «Сайт для компьютера»). */
export interface SiteConfig {
  heroImage?: string;
  heroMotion?: boolean;
  heroSteam?: { x: number; y: number } | null;
  catalogHeroImage?: string;
  equipmentImage?: string;
  b2bImage?: string;
  b2bImagePos?: "left" | "center" | "right";
  categoryImages?: Record<string, string>;
  instagram?: string;
  youtube?: string;
  telegramChannel?: string;
}
/** "Для бизнеса" page of the desktop website (Settings → «Для бизнеса»). Empty texts → built-in defaults. */
type BL3 = { ru: string; uz: string; en: string };
export type BusinessIcon = "bean" | "machine" | "people" | "truck" | "cup" | "award" | "leaf" | "gear";
export interface BusinessSegment {
  enabled?: boolean;
  label?: BL3;
  image?: string;
  title?: BL3;
  text?: BL3;
  cards?: Array<{ icon: BusinessIcon; title: BL3; text: BL3 }>;
}
export interface BusinessContent {
  heroImage?: string;
  heroEyebrow?: BL3;
  heroTitle?: BL3;
  heroText?: BL3;
  ticks?: BL3[];
  showSegments?: boolean;
  segments?: { cafe?: BusinessSegment; restaurant?: BusinessSegment; office?: BusinessSegment };
  showLogos?: boolean;
  logosTitle?: BL3;
  logos?: Array<{ image: string; name: string; url: string }>;
  showCta?: boolean;
  ctaTitle?: BL3;
  ctaText?: BL3;
}
export interface AppConfig {
  site?: SiteConfig;
  business?: BusinessContent;
  features: { subscriptions: boolean; referrals: boolean; events?: boolean; passport?: boolean; birthday?: boolean };
  exhibition?: Exhibition | null;
  botUsername?: string | null;
  delivery: { yandexFeeUzs: number; freeThresholdUzs: number };
  scarcityThreshold: number;
  socialProofMin: number;
  theme: SeasonTheme | null;
}

export const DEFAULT_CONFIG: AppConfig = {
  features: { subscriptions: false, referrals: false, events: false, passport: false },
  exhibition: null,
  botUsername: null,
  delivery: { yandexFeeUzs: 40000, freeThresholdUzs: 0 },
  scarcityThreshold: 5,
  socialProofMin: 3,
  theme: null,
};

let configPromise: Promise<AppConfig> | null = null;
/** Cached for the session: feature flags don't change while the app is open. */
export function fetchConfig(): Promise<AppConfig> {
  configPromise ??= http<AppConfig>("/api/config").catch(() => DEFAULT_CONFIG);
  return configPromise;
}

export async function fetchMarketingConsent(): Promise<boolean> {
  return (await http<{ marketingConsent: boolean }>("/api/me/marketing")).marketingConsent;
}

export async function setMarketingConsent(consent: boolean): Promise<boolean> {
  return (await http<{ marketingConsent: boolean }>("/api/me/marketing", { method: "PATCH", body: JSON.stringify({ consent }) })).marketingConsent;
}

export interface ReferralInfo {
  link: string | null;
  code: string;
  invited: number;
  qualified: number;
}

export async function fetchReferral(): Promise<ReferralInfo> {
  return http<ReferralInfo>("/api/referral");
}

export interface Subscription {
  id: string;
  productId: string;
  variantId: string;
  quantity: number;
  intervalWeeks: number;
  nextDate: string;
  status: "active" | "paused" | "cancelled";
  product: { name: string; slug: string; images: string[]; translations?: unknown };
}

function localizeSub(s: Subscription): Subscription {
  return { ...s, product: { ...s.product, name: pickLang<{ name: string }>(s.product).name ?? s.product.name } };
}

export async function fetchSubscriptions(): Promise<Subscription[]> {
  return (await http<Subscription[]>("/api/subscriptions")).map(localizeSub);
}

export async function fetchSubscription(id: string): Promise<Subscription> {
  return localizeSub(await http<Subscription>(`/api/subscriptions/${encodeURIComponent(id)}`));
}

export async function createSubscription(input: { productId: string; variantId: string; quantity: number; intervalWeeks: number }): Promise<Subscription> {
  return localizeSub(await http<Subscription>("/api/subscriptions", { method: "POST", body: JSON.stringify(input) }));
}

export async function updateSubscription(id: string, input: Partial<Pick<Subscription, "status" | "quantity" | "intervalWeeks">>): Promise<Subscription> {
  return localizeSub(await http<Subscription>(`/api/subscriptions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) }));
}

// ---- Bundles, suggestions, stories ----

type WithTr = { translations?: unknown };

export async function fetchBundles(): Promise<Bundle[]> {
  const list = await http<Array<Bundle & WithTr & { items: Array<Bundle["items"][number] & WithTr> }>>("/api/bundles");
  return list.map((b) => ({
    ...b,
    ...pickLang<{ title: string; description: string }>(b),
    items: b.items.map((i) => ({ ...i, productName: pickLang<{ name: string }>(i as WithTr).name ?? i.productName })),
  }));
}

export async function fetchSuggestions(productIds: string[]): Promise<Product[]> {
  return (await http<Product[]>("/api/cart/suggestions", { method: "POST", body: JSON.stringify({ productIds }) })).map(localizeProduct);
}

export async function fetchStories(): Promise<Story[]> {
  return (await http<Array<Story & WithTr>>("/api/stories")).map((s) => ({ ...s, ...pickLang<{ title: string; excerpt: string }>(s) }));
}

// ---- Review photos, pickup arrival, wishlists ----

export async function uploadReviewPhotos(files: File[]): Promise<string[]> {
  const form = new FormData();
  files.slice(0, 3).forEach((f) => form.append("file", f, f.name));
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/uploads/review`, { method: "POST", body: form, headers: authHeaders() });
  } catch {
    throw new ApiError("Network error", 0, "NETWORK");
  }
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } | string; message?: string };
      // Our API: { error: { message } }; Fastify default: { error: "Bad Request", message }
      message = (typeof body.error === "object" ? body.error?.message : undefined) ?? body.message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiError(message, res.status);
  }
  return ((await res.json()) as { urls: string[] }).urls;
}

export async function markArriving(orderId: string): Promise<{ arrivingAt: string }> {
  return http(`/api/orders/${encodeURIComponent(orderId)}/arriving`, { method: "POST", body: JSON.stringify({}) });
}

export async function createWishlist(productIds: string[]): Promise<{ code: string; link: string | null }> {
  return http("/api/wishlists", { method: "POST", body: JSON.stringify({ productIds }) });
}

export async function fetchWishlist(code: string): Promise<{ ownerName: string | null; products: Product[] }> {
  const res = await http<{ ownerName: string | null; products: Product[] }>(`/api/wishlists/${encodeURIComponent(code)}`);
  return { ...res, products: res.products.map(localizeProduct) };
}

/** Fire-and-forget POST for analytics; never throws, never blocks the UI. */
export function http_post_silent(path: string, body: unknown): void {
  http(path, { method: "POST", body: JSON.stringify(body) }).catch(() => {});
}

// ---- Leads ----
export type LeadSegment = "horeca" | "home_office" | "other";
export type LeadInterest = "beans" | "equipment" | "training" | "service";
export type LeadVolume = "до 5 кг" | "5–20 кг" | "20+ кг" | "20–50 кг" | "50+ кг" | "не знаю";
export interface LeadInput {
  segment: LeadSegment;
  fullName: string;
  city: string;
  orgName?: string;
  phone: string;
  canTelegram: boolean;
  interests: LeadInterest[];
  volume?: LeadVolume;
  comment?: string;
  source: string;
}
export const submitLead = (input: LeadInput) => http<{ id: string; duplicate: boolean }>("/api/leads", { method: "POST", body: JSON.stringify(input) });
/** Lead from a guest of the desktop website (not signed in). `website` is a honeypot field — always empty for people. */
export const submitWebLead = (input: Omit<LeadInput, "canTelegram"> & { consent: true; website: string }) =>
  http<{ id: string; duplicate: boolean }>("/api/leads/web", { method: "POST", body: JSON.stringify(input) });

// ---- Addresses & delivery slots ----
export interface SavedAddress {
  id: string;
  label: string;
  fullName: string;
  phone: string;
  address: string;
  comment?: string | null;
}
export const fetchAddresses = () => http<SavedAddress[]>("/api/addresses");
export const saveAddress = (a: Omit<SavedAddress, "id">) => http<SavedAddress>("/api/addresses", { method: "POST", body: JSON.stringify(a) });
export const deleteAddress = (id: string) => http(`/api/addresses/${encodeURIComponent(id)}`, { method: "DELETE" });
export const fetchDeliverySlots = () => http<{ slots: string[]; days: Array<{ date: string; slots: string[] }> }>("/api/delivery/slots");

// ---- Price watch, feedback, cart snapshot ----
export const fetchPriceWatch = () => http<string[]>("/api/price-watch");
export const watchPrice = (productId: string) => http("/api/price-watch", { method: "POST", body: JSON.stringify({ productId }) });
export const unwatchPrice = (productId: string) => http(`/api/price-watch/${encodeURIComponent(productId)}`, { method: "DELETE" });
export const sendFeedback = (orderId: string, f: { rating: number; reasons: string[]; comment?: string }) =>
  http(`/api/orders/${encodeURIComponent(orderId)}/feedback`, { method: "POST", body: JSON.stringify(f) });
export function putCartSnapshot(lines: CartLine[], bundles: BundleLine[]): void {
  http("/api/cart/snapshot", { method: "PUT", body: JSON.stringify({ lines, bundles }) }).catch(() => {});
}

// ---- Recommendations ----
export type RecoReason = { code: "same_origin" | "same_roast" | "similar_taste" | "new" | "popular"; ref?: string };
export type RecoProduct = Product & { reason: RecoReason };
export async function fetchRecommendations(): Promise<{ personal: boolean; items: RecoProduct[] }> {
  const r = await http<{ personal: boolean; items: RecoProduct[] }>("/api/recommendations");
  return { personal: r.personal, items: r.items.map((p) => ({ ...localizeProduct(p), reason: p.reason })) };
}

// ---- Recipes ----
export interface RecipeStep {
  text: string;
  seconds: number;
  pourToMl?: number | null;
}
export interface Recipe {
  id: string;
  slug: string;
  method: string;
  title: string;
  description: string;
  coverImage: string | null;
  coffeeGrams: number;
  waterMl: number;
  tempC: number | null;
  grind: string;
  steps: RecipeStep[];
  productIds: string[];
}
function localizeRecipe(r: Recipe & WithTr): Recipe {
  const tr = pickLang<{ title: string; description: string; steps: string[] }>(r);
  return {
    ...r,
    ...(tr.title ? { title: tr.title } : {}),
    ...(tr.description ? { description: tr.description } : {}),
    steps: r.steps.map((s, i) => ({ ...s, text: tr.steps?.[i]?.trim() || s.text })),
  };
}
export async function fetchRecipes(): Promise<Recipe[]> {
  return (await http<Array<Recipe & WithTr>>("/api/recipes")).map(localizeRecipe);
}
export async function fetchRecipe(slug: string): Promise<{ recipe: Recipe; products: Product[] }> {
  const r = await http<{ recipe: Recipe & WithTr; products: Product[] }>(`/api/recipes/${encodeURIComponent(slug)}`);
  return { recipe: localizeRecipe(r.recipe), products: r.products.map(localizeProduct) };
}

// ---- Gift certificates & gift orders ----
export const checkGiftCard = (code: string) =>
  http<{ code: string; balanceUzs: number; expiresAt: string | null }>("/api/gift-cards/check", { method: "POST", body: JSON.stringify({ code }) });
export interface GiftView {
  code: string;
  fromName: string | null;
  message: string | null;
  items: Array<{ productName: string; variantLabel: string; quantity: number }>;
  paid: boolean;
  claimed: boolean;
  status: string;
  isBuyer: boolean;
  deliveryMethod: DeliveryMethod | null;
  deliveryDate: string | null;
  deliverySlot: string | null;
}
export const fetchGift = (code: string) => http<GiftView>(`/api/gifts/${encodeURIComponent(code)}`);
export interface GiftClaimInput {
  fullName: string;
  phone: string;
  deliveryMethod: "pickup" | "yandex";
  address?: string;
  comment?: string;
  deliveryDate?: string;
  deliverySlot?: string;
}
export const claimGift = (code: string, input: GiftClaimInput) => http<{ ok: boolean }>(`/api/gifts/${encodeURIComponent(code)}/claim`, { method: "POST", body: JSON.stringify(input) });

// ---- Share to story ----
export const fetchStoryShare = (slug: string) => http<{ imageUrl: string | null; link: string | null }>(`/api/share/story/${encodeURIComponent(slug)}`);

// ---- Events (feature flag) ----
export interface ShopEvent {
  id: string;
  slug: string;
  title: string;
  description: string;
  coverImage: string | null;
  startsAt: string;
  durationMin: number;
  place: string;
  capacity: number;
  priceUzs: number;
  seatsLeft: number;
}
export interface EventBooking {
  id: string;
  code: string;
  seats: number;
  status: string;
  fullName: string;
  event: ShopEvent;
}
export async function fetchEvents(): Promise<ShopEvent[]> {
  return (await http<Array<ShopEvent & WithTr>>("/api/events")).map((e) => ({ ...e, ...pickLang<{ title: string; description: string }>(e) }));
}
export const fetchMyBookings = () => http<EventBooking[]>("/api/events/bookings");
export const bookEvent = (id: string, input: { fullName: string; phone: string; seats: number }) =>
  http<EventBooking>(`/api/events/${encodeURIComponent(id)}/book`, { method: "POST", body: JSON.stringify(input) });
export const cancelBooking = (id: string) => http(`/api/events/bookings/${encodeURIComponent(id)}/cancel`, { method: "POST", body: JSON.stringify({}) });

// ---- FAQ ----
export type FaqCategory = "order" | "delivery" | "returns" | "coffee" | "business" | "other";
export interface FaqItem {
  id: string;
  category: FaqCategory;
  question: string;
  answer: string;
}
export async function fetchFaq(): Promise<FaqItem[]> {
  const list = await http<Array<FaqItem & WithTr>>("/api/faq");
  return list.map((i) => {
    const tr = pickLang<{ question: string; answer: string }>(i);
    return { id: i.id, category: i.category, question: tr.question?.trim() || i.question, answer: tr.answer?.trim() || i.answer };
  });
}
export const faqViewed = (id: string) => http_post_silent(`/api/faq/${encodeURIComponent(id)}/view`, {});
export const faqFeedback = (id: string, helpful: boolean) => http(`/api/faq/${encodeURIComponent(id)}/feedback`, { method: "POST", body: JSON.stringify({ helpful }) });

// ---- "Мой вкус" ----
export type TasteArchetype = "fruity_explorer" | "floral_aesthete" | "sweet_balance" | "bold_classic" | "open_mind";
export interface TasteProfile {
  likes: number;
  dislikes: number;
  minLikes: number;
  archetype: TasteArchetype | null;
  scales: { acidity: number | null; sweetness: number | null; body: number | null; bitterness: number | null };
  roast: number | null;
  notes: Array<{ note: string; count: number }>;
  votes: Array<{ productId: string; liked: boolean }>;
  toRate: Array<{ id: string; slug: string; name: string; images: string[]; translations?: unknown }>;
}
export async function fetchTaste(): Promise<TasteProfile> {
  const r = await http<TasteProfile>("/api/taste");
  return { ...r, toRate: r.toRate.map((p) => ({ ...p, ...pickLang<{ name: string }>(p as Translatable) })) };
}
export const voteTaste = (productId: string, liked: boolean) => http("/api/taste", { method: "POST", body: JSON.stringify({ productId, liked }) });

// ---- Birthday ----
export interface Birthday {
  day: number | null;
  month: number | null;
  year: number | null;
}
export const fetchBirthday = () => http<Birthday>("/api/me/birthday");
export const saveBirthday = (b: Birthday) => http("/api/me/birthday", { method: "PUT", body: JSON.stringify(b) });

// ---- Desktop site sign-in (Telegram Login Widget) ----
export interface TelegramWidgetUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}
export const loginWithTelegram = (user: TelegramWidgetUser) =>
  http<{ token: string; customer: WebProfile }>("/api/auth/telegram-login", { method: "POST", body: JSON.stringify(user) });
export const logoutWeb = () => http<{ ok: boolean }>("/api/auth/logout", { method: "POST", body: JSON.stringify({}) });

// ---- Desktop website: "О нас" page (admin → Настройки → «Страница „О нас“») ----
type L3 = { ru: string; uz: string; en: string };
export interface AboutContent {
  heroEyebrow?: L3;
  contactText?: L3;
  heroTitle: L3;
  heroText: L3;
  heroImage: string;
  stats: Array<{ value: string; label: L3 }>;
  storyTitle: L3;
  storyText: L3;
  storyImage: string;
  values: Array<{ title: L3; text: L3 }>;
  gallery: string[];
}
export const fetchAbout = () => http<AboutContent>("/api/about");

// ---- "Спросить бариста": website chat (desktop) ----
const CHAT_TOKEN_KEY = "kms.chatToken";
let memChatToken = "";
function chatToken(): string {
  try {
    return localStorage.getItem(CHAT_TOKEN_KEY) ?? memChatToken;
  } catch {
    return memChatToken;
  }
}
export interface WebChatMessage {
  id: string;
  fromStaff: boolean;
  author: string | null;
  text: string;
  createdAt: string;
}
export interface WebChatState {
  chat: { shortNo: number; name: string | null } | null;
  messages: WebChatMessage[];
  online: boolean;
  hours: string;
}
export const hasWebChat = (): boolean => !!chatToken() || !!getWebSession();
export function fetchWebChat(after?: string): Promise<WebChatState> {
  const t = chatToken();
  return http<WebChatState>(`/api/webchat${after ? `?after=${encodeURIComponent(after)}` : ""}`, { headers: t ? { "X-Chat-Token": t } : {} });
}
export async function sendWebChat(input: { text: string; name?: string; phone?: string; page?: string; website?: string }): Promise<{ message: WebChatMessage; online: boolean }> {
  const t = chatToken();
  const res = await http<{ token?: string; message: WebChatMessage; online: boolean }>("/api/webchat", {
    method: "POST",
    body: JSON.stringify(input),
    headers: t ? { "X-Chat-Token": t } : {},
  });
  if (res.token) {
    memChatToken = res.token;
    try {
      localStorage.setItem(CHAT_TOKEN_KEY, res.token);
    } catch {
      /* private mode: the chat lasts for this visit */
    }
  }
  return res;
}
